import { orderBy } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { BackendMeasure } from "types/backend";
import {
  Measure,
  MeasureImpact,
  FormMeasureProperty,
  MeasureConf,
  ClimateRisk,
  FilterOption,
} from "types/types";

export function deserializeMeasure(measure: BackendMeasure): Measure {
  const { measurePropertyLinkList, ...rest } = measure;
  const formattedFormMeasureProperties = measurePropertyLinkList.map(
    (property) => {
      return {
        ...property,
        value: property.defaultValue,
      };
    }
  );
  return { ...rest, measurePropertyLinkList: formattedFormMeasureProperties };
}

export function useFilteredMeasuresFilterSelection(
  selectedFilter: FilterOption,
  measureImpacts: MeasureImpact[]
) {
  if (selectedFilter.value === "rel") {
    return orderBy(measureImpacts, ["score", "measure.name"], ["desc", "asc"]);
  }
  if (selectedFilter.value === "asc") {
    return orderBy(measureImpacts, ["measure.name"], ["asc"]);
  }
  if (selectedFilter.value === "desc") {
    return orderBy(measureImpacts, ["measure.name"], ["desc"]);
  }

  return orderBy(
    measureImpacts,
    [
      (measureImpact) => {
        const foundTheme = measureImpact.themeScoreList.find(
          (themeScore) => themeScore.theme.name === selectedFilter.value
        );
        return foundTheme?.score;
      },
      "measure.name",
    ],
    ["desc", "asc"]
  );
}

export function useFilteredMeasures(
  measures: MeasureImpact[],
  search: string,
  selectedMeasure?: MeasureImpact
) {
  const { pathname } = useLocation();
  const isHardenedSurface = pathname.includes("hardened-surface");

  // TODO: This is a hack to get the measures to show up in the sidebar
  const filteredMeasures = measures
    .filter((measure) => measure.measure.hardenedSurface === isHardenedSurface)

    .filter((measure) =>
      measure.measure.name.toLowerCase().includes(search.toLowerCase())
    );
  return filteredMeasures;
}

export function useSelectedMeasure(measures: MeasureImpact[]) {
  const { measureId } = useParams();
  const foundMeasure = measures.find(
    (measure) => measure.measure.id.toString() === measureId
  );
  return foundMeasure;
}

export function useTopThreeMeasures(measures: MeasureImpact[]) {
  const filteredMeasures = measures.filter(
    (measure) => measure.measure.hardenedSurface === false
  );
  const orderedMeasures = orderBy(
    filteredMeasures,
    ["score", "measure.name"],
    ["desc", "asc"]
  );
  const topThreeMeasures = orderedMeasures.slice(0, 3);
  return topThreeMeasures;
}

export function useMeasureValidationSchema(
  measureConfigs: MeasureConf[],
  measureConfigId: number
) {
  const { t } = useTranslation();

  return Yup.object().shape({
    measureProperties: Yup.array().of(
      Yup.mixed()
        .required()
        .test("test", function (prop: FormMeasureProperty, ctx) {
          const path = ctx.path + ".value";
          if (prop.value === undefined) {
            return ctx.createError({
              path: path,
              message: t("validation.fieldIsRequired", {
                field: prop.label,
              }),
            });
          }
          if (prop.value < 0) {
            return ctx.createError({
              path: path,
              message: t("validation.valueMustBePositive", {
                field: prop.label,
              }),
            });
          }
          if (prop.maximum && prop.value > prop.maximum) {
            return ctx.createError({
              path: path,
              message: t("validation.fieldIsTooLarge", {
                field: prop.label,
                max: prop.maximum,
                unit: prop.unit,
              }),
            });
          }
          if (prop.minimum && prop.value < prop.minimum) {
            return ctx.createError({
              path: path,
              message: t("validation.fieldIsTooSmall", {
                field: prop.label,
                min: prop.minimum,
                unit: prop.unit,
              }),
            });
          }

          return true;
        })
    ),
  });
}

export function useInitialValues(
  measure: Measure,
  measureConfigId: number,
  measureConfigurations: MeasureConf[]
) {
  const foundMeasureConfig = measureConfigurations.find(
    (measureConfig) => measureConfig.id === measureConfigId
  );

  if (!foundMeasureConfig || foundMeasureConfig?.propertyValueList.length === 0)
    return { measureProperties: measure.measurePropertyLinkList };

  return { measureProperties: foundMeasureConfig.propertyValueList };
}

export function useUrlFilterSelection(filterOptions: FilterOption[]) {
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter");
  const [firstFilterOption] = filterOptions;
  const selectedFilter =
    filterOptions.find((option) => option.value === filter) ||
    firstFilterOption;

  function updateFilterSelectionParam(filter: string) {
    const newParams = new URLSearchParams(params);
    newParams.set("filter", filter);
    setParams(newParams);
  }

  return { selectedFilter, updateFilterSelectionParam };
}

export function useFilterOptions(
  climateRisks: ClimateRisk[] = []
): FilterOption[] {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isHardenedSurface = pathname.includes("hardened-surface");

  const baseOptions: FilterOption[] = [
    { label: t("label.alphabeticAsc"), value: "asc" },
    { label: t("label.alphabeticDesc"), value: "desc" },
  ];

  if (isHardenedSurface) return [...baseOptions];

  const themeOptions: FilterOption[] = climateRisks.map((climateRisk) => {
    return {
      label: t("label.themeLabel", { name: climateRisk.theme.name }),
      value: climateRisk.theme.name,
    };
  });

  return [
    { label: t("label.relevance"), value: "rel" },
    ...baseOptions,
    ...themeOptions,
  ];
}
